import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Zap, Shield } from 'lucide-react';
import SEO from '@/components/SEO';
import '@/styles/forms.css';
import '@/styles/landing.css';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact UXAuditPro',
  url: 'https://www.uxauditpro.com/contact/',
  description: 'Get in touch with the UXAuditPro team for support, sales enquiries, or partnership opportunities.',
  mainEntity: {
    '@type': 'Organization',
    name: 'UXAuditPro',
    url: 'https://www.uxauditpro.com/',
    email: 'hello@uxauditpro.com',
  },
};

const topics = [
  { value: 'support', label: 'Product Support' },
  { value: 'sales', label: 'Sales / Upgrade Enquiry' },
  { value: 'agency', label: 'Agency / White-label' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'press', label: 'Press / Media' },
  { value: 'other', label: 'Other' },
];

const channels = [
  {
    icon: <Mail className="w-5 h-5" />,
    title: 'Email',
    body: 'For general enquiries and support.',
    link: 'mailto:hello@uxauditpro.com',
    linkLabel: 'hello@uxauditpro.com',
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: 'Fast Support',
    body: 'Use the chat bubble on any page for the quickest response.',
    link: null,
    linkLabel: 'Use the chat widget →',
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: 'Security Issues',
    body: 'Report vulnerabilities responsibly.',
    link: 'mailto:security@uxauditpro.com',
    linkLabel: 'security@uxauditpro.com',
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('success');
      setForm({ name: '', email: '', topic: '', message: '' });
    } catch {
      // Fallback: open mailto if API unavailable
      setStatus('success');
      window.location.href = `mailto:hello@uxauditpro.com?subject=Contact%20from%20${encodeURIComponent(form.name)}&body=${encodeURIComponent(form.message)}`;
    }
  };

  return (
    <>
      <SEO
        title="Contact UXAuditPro: Support & Sales Enquiries"
        description="Get in touch with the UXAuditPro team for product support, sales enquiries, agency white-label partnerships, and any other questions. We respond fast."
        canonical="/contact"
        structuredData={structuredData}
      />

      <main style={{ minHeight: '100vh' }}>

        {/* Header */}
        <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
          <div className="uxa-hero-grid" aria-hidden="true" />
          <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto' }}>
          <div>
            <span
              className="inline-block mb-6"
              style={{ padding: '6px 14px', borderRadius: 'var(--radius-pill)', backgroundColor: 'var(--accent-tint)', color: 'var(--accent-text)', fontSize: 'var(--type-dense-size)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              Contact
            </span>
            <h1
              className="uxa-h1 mb-4"
              style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}
            >
              We'd love to hear from you.
            </h1>
            <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: '56ch' }}>
              Reach out for support, sales questions, or just to say hi.
            </p>
          </div>
        </div>
        </header>

        {/* --pb-xl PAIRS WITH THE --pt-xl THAT WAS ALREADY HERE.
            This is the last section on the page and /contact/ has no
            .uxa-closing band, so nothing sat between the "Agency plans?"
            dark panel and the footer: .uxa-pt-xl is padding-TOP only
            (index.css:420), and the measured gap was 0px -- the panel's
            ink edge butted straight into the footer's sand.

            Pages that end on .uxa-closing are meant to touch the footer;
            that band is full-bleed and carries its own bottom padding. A
            page without one needs the bottom half of the rhythm stated.
            .uxa-pb-xl already exists at index.css:421 as the pair to
            .uxa-pt-xl, so this adds no class, no token and no new value --
            96px top, 96px bottom, the same section rhythm as every other
            landing section. */}
        <section className="uxa-landing-section uxa-pt-xl uxa-pb-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {/* Contact channels */}
            <div className="space-y-5">
              {channels.map((c, i) => (
                <div
                  key={c.title}
                  /* height: auto OVERRIDES .uxa-feature-card's height: 100%.
                     THIS SHIPPED BROKEN. The class is written for cards sitting
                     side by side in a grid row, where filling the row is the
                     point. These four are STACKED in one column of a 3-up grid
                     whose row height is set by the tall form beside them, so
                     each card expanded to the full column height and the
                     stack ran through the footer and off the bottom of the
                     page. Live on /contact/ until the owner reported it. */
                  className="uxa-feature-card"
                  style={{ height: 'auto' }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent-text)' }}
                  >
                    {c.icon}
                  </div>
                  <h3 className="uxa-h3 mb-1" style={{ color: 'var(--ink-1)' }}>{c.title}</h3>
                  <p className="uxa-dense mb-2" style={{ color: 'var(--ink-2)' }}>{c.body}</p>
                    {/* 44px MINIMUM. Measured at 121x16 and 139x16 on the
                        built page at all four widths. These are the card's
                        action, not a link inside a sentence, so WCAG 2.5.8's
                        inline-prose exemption does not reach them. Presented
                        as a 44px hit area rather than a 44px box, the same
                        trick .uxa-submit--tertiary already uses: the inline-flex
                        keeps the text where it is. */}
                  {c.link ? (
                    <a
                      href={c.link}
                      className="text-sm font-medium"
                      style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}
                    >
                      {c.linkLabel}
                    </a>
                  ) : (
                    <span className="text-sm font-medium" style={{ color: 'var(--ink-2)' }}>{c.linkLabel}</span>
                  )}
                </div>
              ))}

              {/* The page's one tonal break, on the system's dark panel. */}
              <div className="uxa-dark-panel" style={{ padding: 'var(--space-8)', borderRadius: 'var(--radius-lg)' }}>
                <MessageSquare className="w-5 h-5 text-white mb-3" />
                <p className="text-sm font-bold text-white mb-1">Agency plans?</p>
                <p className="uxa-dense mb-3" style={{ color: 'var(--ink-4)' }}>
                  We offer white-label reports and volume pricing for agencies.
                </p>
                <Link
                  to="/ux-auditor/#pricing"
                  className="text-sm font-medium"
                  /* 44px MINIMUM — measured at 122x16. Same call as the two
                     mailto links above. */
                  style={{ color: 'var(--accent-soft)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}
                >
                  See agency pricing →
                </Link>
              </div>
            </div>

            {/* Contact form */}
            <div className="md:col-span-2">
              <div
                className="uxa-form-card"
                style={{ padding: '40px' }}
              >
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <span
                      className="inline-flex items-center justify-center"
                      /* --success STAYS HERE. tokens.css scopes it to "form
                         validation only", and this is the success state of a
                         form submission — the one place on the site it is
                         correct. Every other use stripped in this phase was
                         decoration. */
                      style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--success)', color: 'var(--on-accent)' }}
                      aria-hidden="true"
                    >
                      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 8px' }}>Message sent</h2>
                    <p className="uxa-helper">We&apos;ll get back to you within one business day.</p>
                    <button
                      type="button"
                      onClick={() => setStatus('idle')}
                      className="uxa-button-secondary"
                      style={{ marginTop: '24px' }}
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" noValidate aria-busy={status === 'loading' ? 'true' : undefined}>
                    <h2 className="uxa-h2" style={{ color: 'var(--ink-1)' }}>Send us a message</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="uxa-field">
                        <label className="uxa-label" htmlFor="contact-name">
                          Name <span aria-hidden="true">*</span><span className="sr-only">(required)</span>
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="Jane Smith"
                          className="uxa-input"
                        />
                      </div>
                      <div className="uxa-field">
                        <label className="uxa-label" htmlFor="contact-email">
                          Email <span aria-hidden="true">*</span><span className="sr-only">(required)</span>
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="jane@company.com"
                          className="uxa-input"
                        />
                      </div>
                    </div>

                    <div className="uxa-field">
                      <label className="uxa-label" htmlFor="contact-topic">Topic</label>
                      <span className="uxa-select-wrap">
                        <select
                          id="contact-topic"
                          name="topic"
                          value={form.topic}
                          onChange={handleChange}
                          className="uxa-select"
                          style={{ color: form.topic ? 'var(--ink-1)' : 'var(--ink-2)' }}
                        >
                          <option value="">Select a topic…</option>
                          {topics.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </span>
                    </div>

                    <div className="uxa-field">
                      <label className="uxa-label" htmlFor="contact-message">
                        Message <span aria-hidden="true">*</span><span className="sr-only">(required)</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us what's on your mind…"
                        className="uxa-textarea"
                        aria-invalid={errorMsg ? 'true' : undefined}
                      />
                    </div>

                    {errorMsg && (
                      <p className="uxa-msg uxa-msg--error" role="alert">
                        <span className="uxa-msg-mark" aria-hidden="true">!</span>
                        <span><strong>Error:</strong> {errorMsg}</span>
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      aria-busy={status === 'loading' ? 'true' : undefined}
                      className="uxa-submit uxa-submit--block"
                    >
                      {status === 'loading' ? 'Sending…' : 'Send Message'}
                      <span className="uxa-submit-knob" aria-hidden="true">
                        {status === 'loading' ? <span className="uxa-spinner" /> : <>&rarr;</>}
                      </span>
                    </button>

                    <p className="uxa-helper" style={{ textAlign: 'center' }}>
                      We typically respond within one business day.
                    </p>
                  </form>
                )}
              </div>
            </div>

          </div>
        </section>

      </main>
    </>
  );
}
